import { useEffect, useMemo, useState } from 'react';
import { Icon } from '@iconify/react';
import { Input } from '@/components/shadcn/input';
import { Button } from '@/components/shadcn/button';
import { Card } from '@/components/shadcn/card';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/shadcn/sheet';
import type { PickleballGameListItem, PickleballSessionListItem } from '@/services/pickleballService';
import type { MabarParticipant } from '@/services/communityService';
import { countPlayerAppearances } from './pickleballListUtils';

interface PickleballEditPlayerSheetProps {
  open: boolean;
  game: PickleballGameListItem | null;
  gameNumber: number;
  participants: MabarParticipant[];
  sessions: PickleballSessionListItem[];
  saving: boolean;
  onClose: () => void;
  onSave: (sideA: number[], sideB: number[]) => void;
}

type SlotKey = { side: 'a' | 'b'; index: number };

export default function PickleballEditPlayerSheet({
  open,
  game,
  gameNumber,
  participants,
  sessions,
  saving,
  onClose,
  onSave,
}: PickleballEditPlayerSheetProps) {
  const [sideA, setSideA] = useState<number[]>([]);
  const [sideB, setSideB] = useState<number[]>([]);
  const [picker, setPicker] = useState<SlotKey | null>(null);
  const [query, setQuery] = useState('');
  const lineupIds = useMemo(() => new Set([...sideA, ...sideB].filter(Boolean)), [sideA, sideB]);

  useEffect(() => {
    if (!open || !game) return;
    setSideA(game.sideAPlayers.map((p) => p.participantId));
    setSideB(game.sideBPlayers.map((p) => p.participantId));
    setPicker(null);
    setQuery('');
  }, [open, game]);

  const replaceSlot = (slot: SlotKey, participantId: number) => {
    if (slot.side === 'a') {
      const next = [...sideA];
      next[slot.index] = participantId;
      setSideA(next);
    } else {
      const next = [...sideB];
      next[slot.index] = participantId;
      setSideB(next);
    }
    setPicker(null);
    setQuery('');
  };

  const candidates = participants.filter((p) => {
    const current = picker && (picker.side === 'a' ? sideA[picker.index] : sideB[picker.index]);
    if (lineupIds.has(p.id) && current !== p.id) return false;
    if (query && !p.name.toLowerCase().includes(query.toLowerCase())) return false;
    return true;
  });

  const renderRow = (participantId: number, sideLabel: string, slot: SlotKey) => {
    const name =
      participants.find((p) => p.id === participantId)?.name ||
      [...(game?.sideAPlayers ?? []), ...(game?.sideBPlayers ?? [])].find(
        (p) => p.participantId === participantId
      )?.playerName ||
      'Pemain';
    return (
      <Card
        key={`${slot.side}-${slot.index}`}
        className="flex flex-row items-center justify-between rounded-2xl border-gray-100 px-3 py-3 shadow-none"
      >
        <div>
          <p className="text-sm font-extrabold text-[#0E1726]">{name}</p>
          <p className="text-xs text-gray-400">{sideLabel}</p>
        </div>
        <Button
          type="button"
          variant="ghost"
          className="h-auto gap-1 px-0 text-sm font-semibold text-[#FC480D] hover:bg-transparent hover:text-[#D83B08]"
          onClick={() => setPicker(slot)}
        >
          <Icon icon="mdi:swap-horizontal" className="h-4 w-4" />
          Ganti
        </Button>
      </Card>
    );
  };

  return (
    <>
      <Sheet open={open && picker == null} onOpenChange={(next) => !next && onClose()}>
        <SheetContent side="bottom" className="rounded-t-3xl">
          <SheetHeader>
            <SheetTitle>Edit Player · Game {gameNumber}</SheetTitle>
            <SheetDescription>Pilih pemain pickleball yang mau diganti.</SheetDescription>
          </SheetHeader>
          <div className="mt-4 space-y-2">
            {sideA.map((id, index) => renderRow(id, 'Tim A', { side: 'a', index }))}
            {sideB.map((id, index) => renderRow(id, 'Tim B', { side: 'b', index }))}
          </div>
          <Button
            type="button"
            disabled={saving}
            className="mt-5 h-12 w-full rounded-full bg-[#FC480D] font-bold text-white hover:bg-[#D83B08]"
            onClick={() => onSave(sideA, sideB)}
          >
            Simpan Perubahan
          </Button>
        </SheetContent>
      </Sheet>

      <Sheet open={picker != null} onOpenChange={(next) => !next && setPicker(null)}>
        <SheetContent side="bottom" className="rounded-t-3xl">
          <SheetHeader>
            <SheetTitle>Pilih Pengganti</SheetTitle>
          </SheetHeader>
          <div className="relative mt-3">
            <Icon icon="mdi:magnify" className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cari nama pemain"
              className="h-11 rounded-full pl-9"
            />
          </div>
          <div className="mt-3 max-h-80 space-y-2 overflow-y-auto">
            {candidates.map((player) => (
              <Button
                key={player.id}
                type="button"
                variant="outline"
                className="h-auto w-full justify-between rounded-2xl border-gray-100 px-3 py-3"
                onClick={() => picker && replaceSlot(picker, player.id)}
              >
                <span className="text-left">
                  <span className="block text-sm font-extrabold text-[#0E1726]">{player.name}</span>
                  <span className="block text-xs font-normal text-gray-400">
                    {countPlayerAppearances(sessions, player.id)}x main
                  </span>
                </span>
                <Icon icon="mdi:plus-circle-outline" className="h-5 w-5 text-[#FC480D]" />
              </Button>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
